import { useEffect, useRef, useState } from 'react';
import { authApi } from '../api/auth.api';
import type { ApiError, ErrorState } from '../types/page.type';
import { getErrorMessage } from '@/core/api/error-messages';

export type TokenValidationStatus = 'validating' | 'valid' | 'invalid' | 'error';

export function useValidatePasswordResetToken(token: string | null) {
  const [status, setStatus] = useState<TokenValidationStatus>('validating');
  const [errorState, setErrorState] = useState<ErrorState | null>(null);
  const hasValidated = useRef(false);

  useEffect(() => {
    if (hasValidated.current) return;
    hasValidated.current = true;

    const validate = async () => {
      // react-doctor-disable-next-line react-doctor/no-event-handler -- RHF form.reset on async-loaded detail / controlled-modal prop sync; not derivable in an event handler
      if (!token) {
        const errorMsg = getErrorMessage('AdminUser.InvalidPasswordResetToken');
        setErrorState({ title: errorMsg.title, message: errorMsg.message });
        setStatus('invalid');
        return;
      }

      try {
        await authApi.validatePasswordResetToken(token);
        setStatus('valid');
      } catch (error: unknown) {
        const apiError = error as ApiError;
        const data = apiError?.response?.data;
        const errorCode = data?.title || data?.code || data?.type;
        const statusCode = apiError?.response?.status;
        const errorMsg = getErrorMessage(errorCode, statusCode, data?.detail);
        setErrorState({ title: errorMsg.title, message: errorMsg.message });
        setStatus('error');
      }
    };

    validate();
  }, [token]);

  return { status, errorState };
}
